/**
 * The game simulation (GDD §4, §5): builds the run state and advances it one fixed step in the concept's order
 * (stepGame L1184–L1209, then the guns and tracers of stepRunner L1288–L1295). Pure: no three.js, nothing created
 * per step. The caller moves the jet first (src/sim/flight.ts) and passes the step's world shift.
 */
import { combatSeed, newRunSeed, sectionSeed, seededRng, type Rng } from '../core/rng.ts';
import { createEnemyPool } from '../entities/pool.ts';
import { createBridgeState, resetBridge, stepBridge } from '../entities/bridge.ts';
import { stepEnemies } from '../entities/enemies.ts';
import { stepShots } from '../entities/shots.ts';
import { spawnEnemyJet } from '../difficulty/spawner.ts';
import { checkCollisions } from '../rules/collisions.ts';
import { stepFuel } from '../rules/fuel.ts';
import { stepGuns, stepTracers, tracerCount } from '../rules/guns.ts';
import { newGame, stepLives } from '../rules/lives.ts';
import { shiftCheckpoint } from '../rules/checkpoint.ts';
import { updateHigh } from '../rules/scoring.ts';
import type { WorldProfile } from '../world/profiles.ts';
import type { GameSim, RunState, Streams } from './context.ts';
import type { FlightState } from './flight.ts';
import { NO_FX, type Fx } from './fx.ts';
import { createEnemy, createList, createShot, createTracer, type Rules, type SimEvent } from './state.ts';

/** Enemy shots in flight at once (concept L897). */
export const SHOT_POOL = 48;
/** Enemy-jet triggers outstanding: up to 4 per section, two sections (judgment call 21). */
const JET_TRIGGERS = 8;
/** Events kept for the HUD. */
const EVENT_RING = 16;
/** The concept's demo high score (D20 starts GDD runs at 0). */
const CONCEPT_HIGH = 48200;

export interface GameSimOptions {
  W: WorldProfile;
  /** Terrain height as drawn (bank collisions). */
  ground: (x: number, z: number) => number;
  /** The jet the player or autopilot flies; respawns move it. */
  jet: FlightState;
  rules?: Rules;
  fx?: Fx;
  /** Randomness; default: production streams with `fxRand` as the effects stream. */
  streams?: Streams;
  fxRand?: Rng;
  /** Level of the first run (concept `startLevel`). */
  startLevel?: number;
  /** A player's run (D20); false for the attract-mode demo. */
  player?: boolean;
  /** Saved high score. */
  hi?: number;
  nextRunSeed?: () => number;
  /** Start the first run now (default true, as the concept does at mount, L1440). */
  startRun?: boolean;
}

/** Production streams (judgment call 20): a section stream reseeded per section, a combat stream per run. */
export function productionStreams(fx: Rng): Streams {
  const section = seededRng(0);
  const combat = seededRng(0);
  return {
    section,
    combat,
    fx,
    beginSection(runSeed, index) {
      section.reseed(sectionSeed(runSeed, index));
    },
    beginRun(runSeed) {
      combat.reseed(combatSeed(runSeed));
    },
  };
}

export function createGameSim(o: GameSimOptions): GameSim {
  const rules = o.rules ?? 'gdd';
  const pool = createEnemyPool();
  const capacity = Object.values(pool.types).reduce((n, p) => n + p.records.length, 0);
  const shotSlots = Array.from({ length: SHOT_POOL }, (_, i) => createShot(i));
  const run: RunState = {
    player: o.player ?? false,
    demo: false,
    runSeed: 0,
    level: 1,
    lives: 0,
    score: 0,
    hi: o.hi ?? (rules === 'concept' ? CONCEPT_HIGH : 0),
    fuel: 1,
    alive: true,
    deadT: 0,
    invuln: 0,
    over: 0,
    overShown: false,
    title: 0,
    skill: 0.84,
    bridgeRange: 200,
    backoff: false,
    jetVisible: true,
    firstLevel: o.startLevel ?? 1,
    nextExtra: 0,
    checkpointZ: o.jet.z,
    progressFrom: o.jet.z,
    sectionIndex: 0,
    sectionZA: 0,
    sectionZB: 0,
    runs: 0,
    lastCause: null,
    crashes: 0,
  };
  const sim: GameSim = {
    W: o.W,
    rules,
    ground: o.ground,
    fx: o.fx ?? NO_FX,
    streams: o.streams ?? productionStreams(o.fxRand ?? seededRng(1)),
    jet: o.jet,
    nextRunSeed: o.nextRunSeed ?? newRunSeed,
    run,
    guns: { fireT: 0, burst: 0, next: 0 },
    pool,
    ents: createList(capacity, createEnemy('ship', -1)),
    shotSlots,
    shotUsed: new Uint8Array(SHOT_POOL),
    shots: createList(SHOT_POOL, shotSlots[0] ?? createShot(0)),
    tracers: Array.from({ length: tracerCount(rules) }, createTracer),
    jetTrig: new Float64Array(JET_TRIGGERS),
    jetTrigCount: 0,
    bridge: createBridgeState(o.W),
    slots: new Float64Array(64),
    events: Array.from({ length: EVENT_RING }, (): SimEvent => ({ type: 'levelStart', level: 0, cause: null })),
    eventCount: 0,
    t: 0,
  };
  resetBridge(sim);
  if (o.startRun ?? true) newGame(sim);
  return sim;
}

/**
 * The 800 m floating-origin shift (concept gameShift L1210–L1215), applied before the step it happens in. The
 * bridge becomes the next one, intact. Tracers take the shift inside their own update, as in the concept.
 */
export function shiftGameSim(sim: GameSim, dz: number): void {
  const ents = sim.ents;
  // Judgment call 18: a dying enemy's model moves with the world; the concept leaves it behind.
  const moveDying = sim.rules === 'gdd';
  for (let i = 0; i < ents.count; i++) {
    const e = ents.items[i];
    if (!e) continue;
    e.z += dz;
    if (e.dying <= 0 || moveDying) {
      e.posZ += dz;
      e.prevZ += dz;
    }
  }
  const shots = sim.shots;
  for (let i = 0; i < shots.count; i++) {
    const s = shots.items[i];
    if (!s) continue;
    s.z += dz;
    s.prevZ += dz;
  }
  for (let i = 0; i < sim.jetTrigCount; i++) sim.jetTrig[i] = (sim.jetTrig[i] ?? 0) + dz;
  shiftCheckpoint(sim, dz);
  resetBridge(sim);
}

/**
 * One fixed step after the jet has moved. `t` is the run's clock (bobbing, beacons, rotors), `fire` the pilot's
 * trigger, `shift` this step's world shift (already applied with shiftGameSim).
 */
export function stepGameSim(sim: GameSim, dt: number, t: number, fire: boolean, shift: number): void {
  sim.t = t;
  sim.fx.step(dt);
  stepLives(sim, dt);
  const run = sim.run;
  for (let i = sim.jetTrigCount - 1; i >= 0; i--) {
    if (sim.jet.z < (sim.jetTrig[i] ?? 0) && run.alive) {
      spawnEnemyJet(sim);
      for (let k = i; k < sim.jetTrigCount - 1; k++) sim.jetTrig[k] = sim.jetTrig[k + 1] ?? 0;
      sim.jetTrigCount--;
    }
  }
  stepEnemies(sim, dt, t);
  stepShots(sim, dt);
  stepBridge(sim, dt);
  if (run.alive) {
    checkCollisions(sim);
    stepFuel(sim, dt);
  }
  updateHigh(sim);
  stepGuns(sim, fire, dt);
  stepTracers(sim, dt, shift);
}
